import makeStyles from '@mui/styles/makeStyles';
import React, { Dispatch, FC, ReactElement, useState } from 'react';
import { RaptorTheme, greys, mainColors } from '../../../../styling/theme';
import { roundNumberToTwoDecimalPlaces } from '../../../../utilities/numberFormatters';
import GenericLineChart from '../../../charts/GenericLineChart';
import GridItem from '../../../layout/GridComponents/GridItem';
import HelpIcon from '@mui/icons-material/Help';
import { Button, Tooltip, Typography } from '@mui/material';
import { QueryBoundary } from '../../../utilityComponents/QueryBoundary';
import {
  FundEsgTimeSeriesApiResponse,
  FundEsgTimeSeriesPoint,
  useFundEsgTimeSeriesData,
} from '../../../../api/rs-hg/esg';

interface CarbonFootprintOverviewPanelProps {
  data: any;
  fundId: string | null;
  fundName: string | null;
  positionDate?: string;
}

const useOverviewPanelStyles = makeStyles<RaptorTheme>((theme) => ({
  footprintMetricsContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: 8,
  },
  metricValueContainer: {
    display: 'flex',
    marginTop: '0.62rem',
  },
  numberContainer: {
    marginLeft: '0.31rem',
    borderStyle: 'solid',
    borderRadius: 10,
    borderColor: greys.grey600,
    paddingLeft: 8,
    paddingRight: 8,
    paddingTop: 4,
  },
  title: {
    display: 'flex',
    color: mainColors.mainBlue,
  },
}));

interface CarbonButtonProps {
  displayValue: string;
  keyValue: string;
  currentlySelectedKey: string;
  onClick: Dispatch<string>;
}

interface StyleProps {
  buttonActive: boolean;
}

const useCarbonButtonStyles = makeStyles<RaptorTheme, StyleProps>((theme) => ({
  CarbonButton: {
    backgroundColor: (props) =>
      props.buttonActive ? mainColors.mainBlue_lighter : greys.grey400,
    color: 'white',
    width: '100%',
    maxWidth: '15.62rem',
    maxHeight: '2.50rem',
    '&:hover': {
      backgroundColor: (props) =>
        props.buttonActive ? mainColors.mainBlue_lighter : mainColors.mainBlue,
    },
  },
  PageLinkButtonRootDisabled: {
    backgroundColor: (props) => greys.grey400,
    color: 'black',
    width: '100%',
    maxWidth: '12.50rem',
  },
}));

export function CarbonButton({
  displayValue,
  keyValue,
  currentlySelectedKey,
  onClick,
}: React.PropsWithChildren<CarbonButtonProps>): ReactElement {
  const handleClick = () => {
    onClick(keyValue);
  };
  const classes = useCarbonButtonStyles({
    buttonActive: keyValue === currentlySelectedKey,
  });
  return (
    <Button onClick={handleClick} className={classes.CarbonButton}>
      {displayValue}
    </Button>
  );
}

interface MetricsData {
  carbonFootprint: number;
  carbonIntensity: number;
  totalCarbonEmissions: number;
  averageCarbonIntensity: number;
}

function buildMetricsData(data: any): MetricsData {
  if (!data || !('tcfd' in data)) {
    return {
      carbonFootprint: 0,
      carbonIntensity: 0,
      totalCarbonEmissions: 0,
      averageCarbonIntensity: 0,
    };
  }
  const currentScores = data['tcfd'];
  return {
    carbonFootprint: currentScores.carbon_footprint,
    carbonIntensity: currentScores.carbon_intensity,
    totalCarbonEmissions: currentScores.total_carbon_emissions,
    averageCarbonIntensity: currentScores.weighted_average_carbon_intensity,
  };
}

const CarbonFootprintOverviewPanel: FC<CarbonFootprintOverviewPanelProps> = ({
  data,
  fundId,
  fundName,
  positionDate,
}) => {
  const classes = useOverviewPanelStyles();

  const [currentlySelected, setCurrentlySelected] =
    useState<string>('carbonFootprint');

  const metrics = buildMetricsData(data);

  const fundEsgTimeSeriesQuery = useFundEsgTimeSeriesData(
    fundId ?? undefined,
    positionDate,
  );

  return (
    <>
      <GridItem
        card
        xs={12}
        md={5}
        lg={3}
        cardStyle={{
          padding: 8,
          height: '100%',
        }}
      >
        <div>
          <div className={classes.title}>
            <Typography variant="h2">Carbon Footprint Metrics</Typography>
            <Tooltip
              title={
                <Typography variant="h3" style={{ color: 'white' }}>
                  {'Click on a Metric below to view on Chart.'}
                </Typography>
              }
              placement="top"
            >
              <HelpIcon style={{ cursor: 'pointer' }} />
            </Tooltip>
          </div>
          <div className={classes.metricValueContainer}>
            <CarbonButton
              displayValue="Carbon Footprint"
              keyValue="carbonFootprint"
              onClick={setCurrentlySelected}
              currentlySelectedKey={currentlySelected}
            />
            <div className={classes.numberContainer}>
              <Typography variant="h2">
                {roundNumberToTwoDecimalPlaces(metrics.carbonFootprint)}
              </Typography>
            </div>
          </div>
          <div className={classes.metricValueContainer}>
            <CarbonButton
              displayValue="Carbon Intensity"
              keyValue="carbonIntensity"
              onClick={setCurrentlySelected}
              currentlySelectedKey={currentlySelected}
            />
            <div className={classes.numberContainer}>
              <Typography variant="h2">
                {roundNumberToTwoDecimalPlaces(metrics.carbonIntensity)}
              </Typography>
            </div>
          </div>
        </div>
        <div className={classes.metricValueContainer}>
          <CarbonButton
            displayValue="Total Carbon Emissions"
            keyValue="totalCarbonEmissions"
            onClick={setCurrentlySelected}
            currentlySelectedKey={currentlySelected}
          />
          <div className={classes.numberContainer}>
            <Typography variant="h2">
              {roundNumberToTwoDecimalPlaces(metrics.totalCarbonEmissions)}
            </Typography>
          </div>
        </div>
        <div className={classes.metricValueContainer}>
          <CarbonButton
            displayValue="Average Carbon Intensity"
            keyValue="averageCarbonIntensity"
            onClick={setCurrentlySelected}
            currentlySelectedKey={currentlySelected}
          />
          <div className={classes.numberContainer}>
            <Typography variant="h2">
              {roundNumberToTwoDecimalPlaces(metrics.averageCarbonIntensity)}
            </Typography>
          </div>
        </div>
      </GridItem>
      <GridItem
        card
        xs={12}
        md={7}
        lg={9}
        cardStyle={{ padding: 8, height: '100%' }}
      >
        <QueryBoundary
          isLoading={fundEsgTimeSeriesQuery.isLoading}
          isError={fundEsgTimeSeriesQuery.isError}
          data={fundEsgTimeSeriesQuery.data}
          loadingMessages={['Loading Historical Data...']}
          errorMessage="No Historical Data Found"
          noDataMessage="No Historical Data Found"
          card
        >
          {(timeSeriesData) => (
            <CarbonFootprintTimeseriesPanel
              timeseriesData={timeSeriesData}
              timeseriesKey={currentlySelected}
            />
          )}
        </QueryBoundary>
      </GridItem>
    </>
  );
};

interface CarbonFootprintTimeseriesProps {
  timeseriesData: FundEsgTimeSeriesApiResponse;
  timeseriesKey: string;
}

function getChartTitle(key: string) {
  switch (key) {
    case 'carbonFootprint':
      return 'Carbon Footprint';
    case 'carbonIntensity':
      return 'Carbon Intensity';
    case 'totalCarbonEmissions':
      return 'Total Carbon Emissions';
    case 'averageCarbonIntensity':
      return 'Average Carbon Intensity';
    default:
      return '';
  }
}

function buildOverviewTimeseriesData(
  data: FundEsgTimeSeriesApiResponse,
  key: string,
): FundEsgTimeSeriesPoint[] {
  switch (key) {
    case 'carbonFootprint':
      return data.carbon_footprint?.slice().reverse() ?? [];
    case 'carbonIntensity':
      return data.carbon_intensity?.slice().reverse() ?? [];
    case 'totalCarbonEmissions':
      return data.total_carbon_emissions?.slice().reverse() ?? [];
    case 'averageCarbonIntensity':
      return data.weighted_average_carbon_intensity?.slice().reverse() ?? [];
    default:
      return [];
  }
}

const CarbonFootprintTimeseriesPanel: FC<CarbonFootprintTimeseriesProps> = ({
  timeseriesData,
  timeseriesKey,
}) => {
  const timeseries = buildOverviewTimeseriesData(timeseriesData, timeseriesKey);

  return (
    <GenericLineChart
      title={`${getChartTitle(timeseriesKey)} - Historical Time Series`}
      data={timeseries}
      showTitle
      export={false}
      id={'esg_historical_chart'}
      xAxisDataKey="date"
      yAxisTickFormatter={(value: number) =>
        roundNumberToTwoDecimalPlaces(value)
      }
      tooltipFormatter={(value: number) => roundNumberToTwoDecimalPlaces(value)}
      lines={[
        {
          dataKey: 'value',
          color: mainColors.mainBlue_lighter,
        },
      ]}
      yAxisDomain={[
        (dataMin: number) => {
          return dataMin - Math.abs(dataMin * 0.1);
        },
        (dataMax: number) => {
          return dataMax + Math.abs(dataMax * 0.1);
        },
      ]}
      width={'100%'}
      height={400}
      showDot={false}
      margin={{
        top: 10,
        bottom: 10,
        left: 10,
        right: 40,
      }}
      error={'No Historical ESG Data available'}
    />
  );
};

export default CarbonFootprintOverviewPanel;
